import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FaPaperclip, FaPaperPlane, FaTimes, FaUndo } from 'react-icons/fa';
import ConfirmarAcaoModal from './ConfirmarAcaoModal';
import { baixarBlob, brl } from './PlanilhaEmissaoAereo';
import api from '../services/api';
import notify from '../services/notify';

const API = process.env.REACT_APP_API_URL;

// "14/10/2026 10:32 · Nome": quando e quem fechou ou despachou.
export const quandoQuem = (data, quem) => [data ? moment(data).format('DD/MM/YYYY HH:mm') : null, quem].filter(Boolean).join(' · ');

// Uma linha de estado do modal: o texto (o anexo baixa no clique), a nota embaixo e o X que tira.
function Estado({ texto, nota, onAbrir, onTirar }) {
    return (
        <div className="d-flex align-items-start gap-2">
            <div style={{ minWidth: 0, flex: 1 }}>
                {onAbrir ? (
                    <button type="button" className="text-truncate d-block" title={texto} onClick={onAbrir}
                        style={{ border: 0, background: 'none', padding: 0, maxWidth: '100%', color: 'var(--fin-ac)', textDecoration: 'underline' }}>
                        {texto}
                    </button>
                ) : (
                    <div className="text-truncate" title={texto} style={{ color: 'var(--fin-tx)' }}>{texto}</div>
                )}
                {nota && <div>{nota}</div>}
            </div>
            <button type="button" className="fin-pp__btn fin-pp__btn--sm" title="Tirar" aria-label="Tirar" onClick={onTirar}>
                <FaTimes size={10} />
            </button>
        </div>
    );
}

// O aviso de que o Aplicar tira o que está gravado, com o Manter que desiste.
function Sai({ texto, onManter }) {
    return (
        <div className="d-flex align-items-center gap-2">
            <span style={{ flex: 1 }}>{texto}</span>
            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={onManter}><FaUndo size={10} /> Manter</button>
        </div>
    );
}

/**
 * O modal da coluna Faturamento do Painel Aéreo (Gerente Aéreo e admin): os totais do evento pelo que foi emitido,
 * e o fechamento e o despacho. Anexar e Despachar mudam só o modal; o Aplicar grava tudo de uma vez
 * (`EventoAereoFechamentoService::aplicar`), e o Cancelar descarta. O anexo fecha; tirar o anexo só o desvincula, e
 * o arquivo fica no storage. `onAplicado()`: o painel relê a linha.
 */
export default function FechamentoAereoModal({ evento, onFechar, onAplicado }) {
    const token = localStorage.getItem('token');
    const [dados, setDados] = useState(null);
    // O que o Aplicar leva: o arquivo escolhido, tirar o anexo gravado e o despacho (true marca, false desfaz, null
    // fica como está).
    const [anexo, setAnexo] = useState(null);
    const [tirarAnexo, setTirarAnexo] = useState(false);
    const [despachado, setDespachado] = useState(null);
    const [aplicando, setAplicando] = useState(false);
    const escolher = useRef(null);

    useEffect(() => {
        api({ method: 'get', action: '/eventoAereoFechamento', token, params: { evento: evento.id } })
            .then(res => setDados(res.data.data ?? null))
            .catch(err => notify.apiError(err, 'Erro ao carregar o fechamento'));
    }, [evento.id, token]);

    const f = dados?.fechamento ?? {};
    const totais = dados?.totais ?? {};
    const mudou = !!anexo || tirarAnexo || despachado !== null;
    // Os valores numa linha e, embaixo, os contadores. Voos são os trechos dos bilhetes emitidos.
    const linhas = [
        [['Total sem FEE', brl(totais.totalSemFee ?? 0)], ['Total com FEE', brl(totais.totalComFee ?? 0)], ['FEE', brl(totais.fee ?? 0)]],
        [['Bilhetes', totais.bilhetes ?? 0], ['Voos', totais.voos ?? 0], ['Participantes', totais.participantes ?? 0]],
    ];

    const aplicar = async () => {
        setAplicando(true);
        try {
            const corpo = new FormData();
            corpo.append('evento', evento.id);
            if (anexo) corpo.append('anexo', anexo);
            if (tirarAnexo) corpo.append('tirarAnexo', '1');
            if (despachado !== null) corpo.append('despachado', despachado ? '1' : '0');
            await axios.post(`${API}/eventoAereoFechamento`, corpo, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Fechamento aplicado');
            onAplicado();
        } catch (err) {
            notify.apiError(err, 'Erro ao aplicar o fechamento');
        } finally {
            setAplicando(false);
        }
    };

    return (
        <ConfirmarAcaoModal aberto titulo="Fechamento e despacho" subtitulo={evento.nome} rotulo="Aplicar" icone="ok"
            processando={aplicando} confirmarPreso={!dados || !mudou} onConfirmar={aplicar} onFechar={onFechar}>
            {/* Os totais do evento pelo que foi emitido: os bilhetes gravados, não o status das linhas. */}
            <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                {linhas.map((itens, i) => (
                    <div key={i} className="d-flex gap-2">
                        {itens.map(([rotulo, valor]) => (
                            <div key={rotulo} style={{ flex: '1 1 0', minWidth: 0, border: '1px solid var(--fin-bd)', borderRadius: 10, padding: '6px 12px', background: 'var(--fin-sf2)' }}>
                                <div className="text-uppercase" style={{ color: 'var(--fin-mt)', fontSize: 10, letterSpacing: '0.05em', fontWeight: 600 }}>{rotulo}</div>
                                <div style={{ color: 'var(--fin-tx)', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>{dados ? valor : '—'}</div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div className="d-flex gap-3">
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <button type="button" className="fin-pp__btn" style={{ width: '100%', justifyContent: 'center' }} disabled={!dados || aplicando}
                        onClick={() => escolher.current?.click()}>
                        <FaPaperclip size={12} /> Anexar
                    </button>
                    <input ref={escolher} type="file" style={{ display: 'none' }}
                        onChange={e => { const arquivo = e.target.files?.[0]; if (arquivo) { setAnexo(arquivo); setTirarAnexo(false); } e.target.value = ''; }} />
                    <div className="fin-pp__cell-muted" style={{ fontSize: 12, marginTop: 8 }}>
                        {anexo ? (
                            <Estado texto={anexo.name} nota="Fecha no Aplicar" onTirar={() => setAnexo(null)} />
                        ) : f.anexo && !tirarAnexo ? (
                            <Estado texto={f.anexo} nota={`Fechado em ${quandoQuem(f.fechadoEm, f.fechadoPor)}`} onTirar={() => setTirarAnexo(true)}
                                onAbrir={() => baixarBlob(`${API}/eventoAereoFechamentoArquivo?evento=${evento.id}`, token, f.anexo)} />
                        ) : f.anexo ? (
                            <Sai texto="O anexo sai no Aplicar, e o evento deixa de estar fechado." onManter={() => setTirarAnexo(false)} />
                        ) : 'Sem anexo: o evento fecha com ele.'}
                    </div>
                </div>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <button type="button" className="fin-pp__btn" style={{ width: '100%', justifyContent: 'center' }}
                        disabled={!dados || aplicando || (despachado ?? !!f.despachadoEm)}
                        onClick={() => setDespachado(f.despachadoEm ? null : true)}>
                        <FaPaperPlane size={12} /> Despachar
                    </button>
                    <div className="fin-pp__cell-muted" style={{ fontSize: 12, marginTop: 8 }}>
                        {despachado === true ? (
                            <Estado texto="Despachado" nota="Marca no Aplicar" onTirar={() => setDespachado(null)} />
                        ) : despachado === false ? (
                            <Sai texto="O despacho sai no Aplicar." onManter={() => setDespachado(null)} />
                        ) : f.despachadoEm ? (
                            <Estado texto="Despachado" nota={quandoQuem(f.despachadoEm, f.despachadoPor)} onTirar={() => setDespachado(false)} />
                        ) : 'Ainda não despachado.'}
                    </div>
                </div>
            </div>
        </ConfirmarAcaoModal>
    );
}
